import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { usePresence } from '@/contexts/PresenceContext'
import type { RecoverRequest } from '../api'
import { useWorkforce } from './api'
import type { PresenceState } from './logic'

/**
 * The workforce, not the work.
 *
 * Every count here comes from live presence and current assignments — both already recorded. What
 * it deliberately does NOT show is anything needing shift hours, because those do not exist: see
 * the note under the per-site table rather than a confident number nobody can stand behind.
 */

const CountIcon = ({ kind }: { kind: 'available' | 'working' | 'away' }) => {
  const c = kind === 'available' ? 'rgb(101,150,58)' : kind === 'working' ? 'rgb(180,110,8)' : 'hsl(215 16% 55%)'
  return (
    <svg className="h-3.5 w-3.5 flex-none" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2.2" aria-hidden>
      <circle cx="12" cy="12" r="9" />
      {kind === 'available' && <path d="m8.5 12 2.5 2.5 4.5-5" />}
      {kind === 'working' && <path d="M12 7.5V12l3 1.8" />}
      {kind === 'away' && <path d="M8 12h8" />}
    </svg>
  )
}


function GoWaitBadge({ n, kind }: { n: number; kind: 'go' | 'wait' }) {
  return (
    <span
      className="inline-flex h-[22px] min-w-[26px] items-center justify-center rounded px-1.5 text-[11px] font-bold tabular-nums text-white"
      style={{ backgroundColor: kind === 'go' ? 'rgb(151,200,100)' : 'rgb(232,96,55)' }}
    >
      {n}
    </span>
  )
}

export function WorkforceView({ rows, requestID }: { rows: RecoverRequest[]   /** Narrows the site demand to one request; the roster stays estate-wide. */
  requestID?: string | null
}) {
  const { t } = useTranslation()
  // Scoped to the selected request, so this view agrees with the queue beside it.
  const workforce = useWorkforce(true, requestID)
  const presence = usePresence()

  const stateOf = (id: number): PresenceState => (presence?.getUserStatus?.(id) ?? 'offline') as PresenceState

  const engineers = workforce.data?.engineers ?? []


  /** Outstanding objects per team, so the busiest skills lead. */
  const teams = useMemo(() => {
    const demand = new Map<string, number>()
    for (const r of rows) {
      const name = r.teamName || t('assignment.noTeam', 'No team recorded')
      demand.set(name, (demand.get(name) ?? 0) + 1)
    }
    const staff = new Map<string, { available: number; working: number; away: number; loggedIn: number }>()
    for (const e of engineers) {
      const name = e.primaryTeam || t('assignment.noTeam', 'No team recorded')
      const cur = staff.get(name) ?? { available: 0, working: 0, away: 0, loggedIn: 0 }
      const s = stateOf(e.id)
      if (s !== 'offline') cur.loggedIn += 1
      if (e.assignmentsWorking > 0) cur.working += 1
      else if (s === 'available') cur.available += 1
      if (s === 'away') cur.away += 1
      staff.set(name, cur)
    }
    return [...demand.entries()]
      .map(([name, outstanding]) => ({ name, outstanding, ...(staff.get(name) ?? { available: 0, working: 0, away: 0, loggedIn: 0 }) }))
      .sort((a, b) => b.outstanding - a.outstanding)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, engineers, presence, t])

  const sites = workforce.data?.sites ?? []

  // The footer used to be a LEGEND — two badges hardcoded to zero, there to teach the colours.
  // It renders the same badge component as the rows above it, so it read as a total of nought
  // startable and nought blocked while every row above showed real demand. Totals answer the
  // colour question just as well and cannot be misread as data, because they ARE data.
  const totalGo = sites.reduce((n, s) => n + Number(s.go || 0), 0)
  const totalWait = sites.reduce((n, s) => n + Number(s.wait || 0), 0)

  return (
    <div className="flex flex-col gap-5">
      {/* The four workforce figures moved UP into the page's single contextual card row:
          this view used to add a second row of its own, so the Resource screen carried eight
          cards where every other view carried four. */}

      <section>
        <div className="mb-2 flex flex-wrap items-baseline gap-x-4 gap-y-1">
          <h3 className="text-sm font-semibold">{t('assignment.bySkill', 'By skill set')}</h3>
          <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1"><CountIcon kind="available" />{t('assignment.available', 'Available')}</span>
            <span className="flex items-center gap-1"><CountIcon kind="working" />{t('assignment.working', 'Working')}</span>
            <span className="flex items-center gap-1"><CountIcon kind="away" />{t('assignment.away', 'Away')}</span>
          </div>
          {teams.length > 4 && (
            <span className="text-[11px] text-muted-foreground">
              {t('assignment.moreTeams', '+{{n}} more teams', { n: teams.length - 4 })}
            </span>
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {teams.slice(0, 4).map((tm) => (
            <div key={tm.name} className="rounded-xl border bg-card p-3.5">
              <div className="truncate text-[13px] font-semibold">{tm.name}</div>
              <div className="mt-0.5 text-[11px] tabular-nums text-muted-foreground">
                {t('assignment.objectsOutstanding', '{{n}} objects outstanding', { n: tm.outstanding })}
              </div>
              <div className="mt-2.5 flex items-center gap-3">
                <span className="flex items-center gap-1 text-xs tabular-nums"><CountIcon kind="available" />{tm.available}</span>
                <span className="flex items-center gap-1 text-xs tabular-nums"><CountIcon kind="working" />{tm.working}</span>
                <span className="flex items-center gap-1 text-xs tabular-nums"><CountIcon kind="away" />{tm.away}</span>
                <span className="ml-auto text-[11px] tabular-nums text-muted-foreground">
                  {t('assignment.nLoggedIn', '{{n}} logged in', { n: tm.loggedIn })}
                </span>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-2 text-[11px] text-muted-foreground">
          {t('assignment.primaryTeamNote',
            'Primary team shown. Secondary skills are counted when candidates are ranked in the assign dialog.')}
        </p>
      </section>

      <section className="overflow-hidden rounded-xl border bg-card">
        <div className="grid grid-cols-[1.4fr_0.7fr_0.7fr_0.7fr_0.7fr_1.3fr_1.6fr] border-b bg-muted/60 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          <div className="px-3 py-2.5">{t('assignment.site', 'Site')}</div>
          <div className="px-2 py-2.5 text-center">{t('assignment.loggedIn', 'Logged in')}</div>
          <div className="px-2 py-2.5 text-center">{t('assignment.working', 'Working')}</div>
          <div className="px-2 py-2.5 text-center">{t('assignment.available', 'Available')}</div>
          <div className="px-2 py-2.5 text-center">{t('assignment.away', 'Away')}</div>
          <div className="px-2 py-2.5">{t('assignment.teamsOnSite', 'Teams on site')}</div>
          <div className="px-2 py-2.5">{t('assignment.demand', 'Demand')}</div>
        </div>

        {sites.map((s) => {
          const here = engineers.filter((e) => e.locationId === s.locationId)
          const st = here.map((e) => stateOf(e.id))
          const loggedIn = st.filter((x) => x !== 'offline').length
          const working = here.filter((e) => e.assignmentsWorking > 0).length
          const away = st.filter((x) => x === 'away').length
          const available = here.filter((e, i) => e.assignmentsWorking === 0 && st[i] === 'available').length
          const teamsHere = [...new Set(here.map((e) => e.primaryTeam).filter(Boolean))] as string[]

          // The gap note is the row's point. Said in words, because "0" in a column is easy to
          // read past and "14 Go objects, 0 engineers on site" is not.
          let gap: string | null = null
          if (here.length === 0 && s.go > 0) {
            gap = t('assignment.gapNoEngineers', '{{n}} Go objects, 0 engineers on site', { n: s.go })
          } else if (s.go > 0 && teamsHere.length > 0) {
            const missing = s.teamsInDemand.filter((d) => !teamsHere.includes(d))
            if (missing.length) gap = t('assignment.gapNoCover', 'no {{team}} cover', { team: missing[0] })
            else if (here.length === 1) gap = t('assignment.gapOneDeep', '{{team}} cover one deep', { team: teamsHere[0] })
          }

          return (
            <div key={s.locationId}
                 className="grid grid-cols-[1.4fr_0.7fr_0.7fr_0.7fr_0.7fr_1.3fr_1.6fr] items-center border-b text-xs last:border-0">
              <div className="truncate px-3 py-2.5 font-medium">{s.location ?? t('assignment.noLocation', 'No location recorded')}</div>
              <div className="px-2 py-2.5 text-center tabular-nums">{loggedIn}</div>
              <div className="px-2 py-2.5 text-center tabular-nums">{working}</div>
              <div className="px-2 py-2.5 text-center tabular-nums">{available}</div>
              <div className="px-2 py-2.5 text-center tabular-nums">{away}</div>
              <div className="truncate px-2 py-2.5 text-muted-foreground">{teamsHere.join(', ') || '—'}</div>
              <div className="flex flex-wrap items-center gap-1.5 px-2 py-2.5">
                <GoWaitBadge n={s.go} kind="go" />
                <GoWaitBadge n={s.wait} kind="wait" />
                {gap && <span className="text-[11px] font-medium" style={{ color: 'rgb(232,96,55)' }}>{gap}</span>}
              </div>
            </div>
          )
        })}

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t bg-muted/20 px-3 py-2.5 text-[11px] text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <GoWaitBadge n={totalGo} kind="go" /> {t('assignment.legendGo', 'startable now')}
          </span>
          <span className="flex items-center gap-1.5">
            <GoWaitBadge n={totalWait} kind="wait" /> {t('assignment.legendWait', 'blocked by dependencies')}
          </span>
          <span className="text-muted-foreground/70">
            {t('assignment.totalsAcrossSites', 'across all sites')}
          </span>
          <span>
            {t('assignment.remoteNote',
              'On-site demand only restricts the local pool — remote-capable Go work can be taken from any site once network and compute are recovered.')}
          </span>
        </div>
      </section>

      {workforce.data?.unavailableFields?.length ? (
        <p className="rounded-lg border px-3 py-2 text-[11px] leading-relaxed"
           style={{ backgroundColor: 'rgba(245,158,11,0.07)', borderColor: 'rgba(245,158,11,0.35)', color: 'rgb(146,88,8)' }}>
          {t('assignment.proposedFields', 'Not shown because the data does not exist yet:')}{' '}
          {workforce.data.unavailableFields.map((f) => `${f.field} — ${f.reason}`).join(' · ')}
        </p>
      ) : null}
    </div>
  )
}
